// 자료 해석 — 표와 그래프를 읽고 증가율·비중·합계·평균·인원을 계산하거나, 옳은 설명을 고른다.
// 오답 보기는 실제로 자주 하는 실수로 만든다: 증가율의 기준을 바꿈(÷ 나중 값), 비율의 단순 평균(가중 평균이어야 함), 퍼센트를 사람 수로 착각.
import type { ChoiceQuestion } from './ChoiceDrill';
import { hasBatchim, int, makeRng, pick, shuffle, type Rng } from './rng';

export interface DataTable { kind: 'table'; title: string; unit: string; head: string[]; rows: (string | number)[][] }
export interface DataChart { kind: 'chart'; title: string; unit: string; labels: string[]; values: number[] }
export interface DataQuestion extends ChoiceQuestion {
  key: string;
  data: DataTable | DataChart;
  ask: string;
  options: string[];
}

export const DATA_COUNT = 6;

const fmt = (x: number, digits = 1) => x.toLocaleString('ko-KR', { maximumFractionDigits: digits });
const r1 = (x: number) => Math.round(x * 10) / 10;
type Made = { key: string; data: DataTable | DataChart; ask: string; right: string; wrong: string[]; steps: string[] } | null;

/** 숫자 보기: 정답 + 자주 하는 실수 + 부족하면 정답 근처 값 — 표시 문자열이 겹치지 않게 */
function numChoices(right: number, mistakes: number[], show: (x: number) => string, spread: number): { right: string; wrong: string[] } {
  const rs = show(right);
  const seen = new Set([rs]);
  const wrong: string[] = [];
  for (const c of [...mistakes, right + spread, right - spread, right + 2 * spread, right - 2 * spread, right + 3 * spread]) {
    if (wrong.length >= 3) break;
    if (!Number.isFinite(c) || c < 0) continue;
    const s = show(c);
    if (!seen.has(s)) {
      seen.add(s);
      wrong.push(s);
    }
  }
  return { right: rs, wrong };
}

// ---- 지역·연도별 표 ----
const YEARS = [2023, 2024, 2025];
const PLACES = [['서울', '부산', '대구', '광주'], ['A 지점', 'B 지점', 'C 지점', 'D 지점'], ['가 공장', '나 공장', '다 공장', '라 공장']];
function regionData(r: Rng) {
  const names = pick(r, PLACES);
  const what = names[0].includes('공장') ? '생산량' : '매출';
  const unit = what === '생산량' ? '천 개' : '억 원';
  const vals = names.map(() => {
    let v = int(r, 80, 300);
    return YEARS.map((_, i) => (i === 0 ? v : (v = Math.max(40, Math.round(v * (1 + int(r, -15, 30) / 100))))));
  });
  const table: DataTable = { kind: 'table', title: `연도별 ${what}`, unit, head: ['구분', ...YEARS.map((y) => `${y}년`)], rows: names.map((n, i) => [n, ...vals[i]]) };
  return { names, vals, what, unit, table };
}
const eunNeun = (w: string) => `${w}${hasBatchim(w) ? '은' : '는'}`;
const iGa = (w: string) => `${w}${hasBatchim(w) ? '이' : '가'}`;
const ida = (w: string) => `${w}${hasBatchim(w) ? '이다' : '다'}`;

function regionQ(r: Rng, lv: number): Made {
  const { names, vals, what, unit, table } = regionData(r);
  const types = ['max', 'sum', ...(lv >= 2 ? ['diff'] : []), ...(lv >= 3 ? ['avg'] : []), ...(lv >= 4 ? ['growth'] : []), ...(lv >= 5 ? ['share'] : []), ...(lv >= 6 ? ['stmt'] : []), ...(lv >= 8 ? ['stmtFalse'] : [])];
  const type = pick(r, types);
  const i = int(r, 0, 3);
  const name = names[i];
  const col = (y: number) => vals.map((v) => v[y]);
  if (type === 'max') {
    const y = int(r, 0, 2), c = col(y), top = Math.max(...c);
    if (c.filter((x) => x === top).length > 1) return null;
    const k = c.indexOf(top);
    return { key: 'max', data: table, ask: `${YEARS[y]}년 ${what}이 가장 많은 곳은?`, right: names[k], wrong: names.filter((_, j) => j !== k), steps: [`${YEARS[y]}년: ${names.map((n, j) => `${n} ${c[j]}`).join(', ')} → ${names[k]}`] };
  }
  if (type === 'sum') {
    const y = int(r, 0, 2), c = col(y), s = c.reduce((a, b) => a + b, 0);
    const ch = numChoices(s, [s - c[int(r, 0, 3)], s + 10, s - 10], (x) => `${fmt(x)} ${unit}`, 20);
    return { key: 'sum', data: table, ask: `${YEARS[y]}년 네 곳 ${what}의 합은?`, ...ch, steps: [`${c.join(' + ')} = ${fmt(s)} ${unit}`] };
  }
  if (type === 'diff') {
    const d = vals[i][2] - vals[i][0];
    if (d === 0) return null;
    const ch = numChoices(Math.abs(d), [Math.abs(vals[i][2] - vals[i][1]), Math.abs(vals[i][1] - vals[i][0])], (x) => `${fmt(x)} ${unit}`, 5);
    return { key: 'diff', data: table, ask: `${eunNeun(name)} 2025년 ${what}이 2023년보다 얼마나 ${d > 0 ? '늘었나' : '줄었나'}?`, ...ch, steps: [`${vals[i][2]} − ${vals[i][0]} = ${d}`] };
  }
  if (type === 'avg') {
    const m = r1((vals[i][0] + vals[i][1] + vals[i][2]) / 3);
    const sorted = [...vals[i]].sort((a, b) => a - b);
    const ch = numChoices(m, [sorted[1], (vals[i][0] + vals[i][2]) / 2], (x) => `${fmt(r1(x))} ${unit}`, 4);
    return { key: 'avg', data: table, ask: `${name}의 3년 평균 ${what}은?`, ...ch, steps: [`(${vals[i].join(' + ')}) ÷ 3 = ${fmt(m)} ${unit}`] };
  }
  if (type === 'growth') {
    const a = vals[i][1], b = vals[i][2];
    if (a === b) return null;
    const g = r1(((b - a) / a) * 100);
    const word = b > a ? '증가율' : '감소율';
    const ch = numChoices(Math.abs(g), [Math.abs(((b - a) / b) * 100), (b / a) * 100, Math.abs(b - a)], (x) => `${fmt(r1(x))}%`, 2);
    return { key: 'growth', data: table, ask: `${name}의 2024년 대비 2025년 ${what} ${word}은? (소수 첫째 자리까지)`, ...ch, steps: [`(${b} − ${a}) ÷ ${a} × 100 = ${fmt(g)}% — 기준은 '대비' 앞의 해(2024년)`] };
  }
  if (type === 'share') {
    const c = col(2), s = c.reduce((x, y) => x + y, 0), sh = r1((c[i] / s) * 100);
    const prev = col(1).reduce((x, y) => x + y, 0);
    const ch = numChoices(sh, [(c[i] / prev) * 100, (c[i] / (s - c[i])) * 100], (x) => `${fmt(r1(x))}%`, 3);
    return { key: 'share', data: table, ask: `2025년 네 곳 ${what}의 합에서 ${iGa(name)} 차지하는 비중은? (소수 첫째 자리까지)`, ...ch, steps: [`합 = ${c.join(' + ')} = ${s}`, `${c[i]} ÷ ${s} × 100 = ${fmt(sh)}%`] };
  }
  // 옳은 것 / 옳지 않은 것
  const facts: { t: string; ok: boolean }[] = [];
  names.forEach((n, j) => facts.push({ t: `${eunNeun(n)} ${what}이 해마다 늘었다.`, ok: vals[j][0] < vals[j][1] && vals[j][1] < vals[j][2] }));
  for (let t = 0; t < 4; t++) {
    const [p, q] = shuffle(r, [0, 1, 2, 3]);
    const ratio = vals[p][2] / vals[q][2];
    if (Math.abs(ratio - 1.5) > 0.06) facts.push({ t: `2025년 ${names[p]}의 ${what}은 ${names[q]}의 1.5배 이상이다.`, ok: ratio >= 1.5 });
  }
  const sums = vals.map((v) => v[0] + v[1] + v[2]);
  const best = sums.indexOf(Math.max(...sums));
  if (sums.filter((s) => s === sums[best]).length === 1) {
    facts.push({ t: `3년 동안 ${what}의 합이 가장 큰 곳은 ${ida(names[best])}.`, ok: true });
    facts.push({ t: `3년 동안 ${what}의 합이 가장 큰 곳은 ${ida(names[(best + int(r, 1, 3)) % 4])}.`, ok: false });
  }
  for (const j of shuffle(r, [0, 1, 2, 3]).slice(0, 2)) {
    const g = (vals[j][2] - vals[j][1]) / vals[j][1];
    if (Math.abs(g - 0.1) > 0.01) facts.push({ t: `${eunNeun(names[j])} 2025년 ${what}이 전년보다 10% 이상 늘었다.`, ok: g >= 0.1 });
  }
  const down = vals.filter((v) => v[2] < v[1]).length;
  facts.push({ t: `2025년에 ${what}이 전년보다 줄어든 곳은 ${down}곳이다.`, ok: true });
  facts.push({ t: `2025년에 ${what}이 전년보다 줄어든 곳은 ${down === 0 ? 1 : down - 1}곳이다.`, ok: false });
  const wantTrue = type === 'stmt';
  const uniq = [...new Map(facts.map((f) => [f.t, f])).values()];
  const rights = shuffle(r, uniq.filter((f) => f.ok === wantTrue));
  const wrongs = shuffle(r, uniq.filter((f) => f.ok !== wantTrue));
  if (!rights.length || wrongs.length < 3) return null;
  return {
    key: type, data: table, ask: wantTrue ? '표에 대한 설명으로 옳은 것은?' : '표에 대한 설명으로 옳지 않은 것은?',
    right: rights[0].t, wrong: wrongs.slice(0, 3).map((f) => f.t), steps: [`${wantTrue ? '옳은' : '옳지 않은'} 설명: ${rights[0].t}`],
  };
}

// ---- 상품별 판매량·단가 ----
function productQ(r: Rng, lv: number): Made {
  const names = pick(r, [['볼펜', '공책', '필통', '지우개'], ['아메리카노', '라떼', '케이크', '쿠키'], ['티셔츠', '바지', '양말', '모자']]);
  const qty = names.map(() => int(r, 2, 30) * 10);
  const price = names.map(() => int(r, 5, 60) * 100);
  const rev = qty.map((q, i) => q * price[i]);
  const table: DataTable = { kind: 'table', title: '상품별 판매 실적', unit: '', head: ['상품', '판매량(개)', '단가(원)'], rows: names.map((n, i) => [n, qty[i], price[i]]) };
  const type = pick(r, ['revMax', ...(lv >= 4 ? ['revTotal'] : []), ...(lv >= 5 ? ['revRatio'] : [])]);
  if (type === 'revMax') {
    const top = Math.max(...rev), k = rev.indexOf(top);
    if (rev.filter((x) => x >= top * 0.97).length > 1) return null; // 3% 안으로 붙으면 헷갈리니 다시
    return { key: 'revMax', data: table, ask: '매출(판매량 × 단가)이 가장 큰 상품은?', right: names[k], wrong: names.filter((_, j) => j !== k), steps: names.map((n, j) => `${n}: ${qty[j]} × ${fmt(price[j])} = ${fmt(rev[j])}원`) };
  }
  if (type === 'revTotal') {
    const s = rev.reduce((a, b) => a + b, 0);
    const ch = numChoices(s, [qty.reduce((a, b) => a + b, 0) * price.reduce((a, b) => a + b, 0) / 4, s - rev[0]], (x) => `${fmt(Math.round(x))}원`, Math.round(s * 0.05 / 100) * 100 || 100);
    return { key: 'revTotal', data: table, ask: '네 상품 매출의 합은?', ...ch, steps: [...names.map((n, j) => `${n} ${fmt(rev[j])}원`), `합 = ${fmt(s)}원`] };
  }
  const [p, q] = shuffle(r, [0, 1, 2, 3]);
  const ratio = r1(rev[p] / rev[q]);
  if (ratio < 1.2 || ratio > 9) return null;
  const ch = numChoices(ratio, [qty[p] / qty[q], price[p] / price[q]], (x) => `${fmt(r1(x))}배`, 0.4);
  return { key: 'revRatio', data: table, ask: `${names[p]} 매출은 ${names[q]} 매출의 약 몇 배인가?`, ...ch, steps: [`${names[p]} ${qty[p]} × ${fmt(price[p])} = ${fmt(rev[p])}원`, `${names[q]} ${qty[q]} × ${fmt(price[q])} = ${fmt(rev[q])}원`, `${fmt(rev[p])} ÷ ${fmt(rev[q])} ≈ ${fmt(ratio)}배`] };
}

// ---- 연령대별 설문 ----
function surveyQ(r: Rng, lv: number): Made {
  const groups = ['20대', '30대', '40대', '50대'];
  const n = groups.map(() => int(r, 10, 40) * 10);
  const p = groups.map(() => int(r, 2, 9) * 10);
  const yes = n.map((x, i) => (x * p[i]) / 100);
  const topic = pick(r, ['주 4일 근무 도입', '회사 근처 공유 오피스 이용', '점심시간 1시간 30분으로 연장']);
  const table: DataTable = { kind: 'table', title: `'${topic}' 찬성 비율`, unit: '', head: ['연령대', '응답자(명)', '찬성(%)'], rows: groups.map((g, i) => [g, n[i], p[i]]) };
  if (lv >= 6 && r() < 0.6) {
    const all = r1((yes.reduce((a, b) => a + b, 0) / n.reduce((a, b) => a + b, 0)) * 100);
    const simple = p.reduce((a, b) => a + b, 0) / 4;
    if (Math.abs(all - simple) < 1) return null; // 단순 평균 함정이 정답과 겹치면 다시
    const ch = numChoices(all, [simple, Math.max(...p), all + 5], (x) => `${fmt(r1(x))}%`, 3);
    return { key: 'overall', data: table, ask: '전체 응답자 가운데 찬성한 사람의 비율은? (소수 첫째 자리까지)', ...ch, steps: [`찬성자 = ${yes.join(' + ')} = ${yes.reduce((a, b) => a + b, 0)}명`, `응답자 = ${n.join(' + ')} = ${n.reduce((a, b) => a + b, 0)}명`, `비율 = ${fmt(all)}% (비율을 그냥 평균 내면 ${fmt(simple)}% — 응답자 수가 달라 틀림)`] };
  }
  const i = int(r, 0, 3);
  const ch = numChoices(yes[i], [p[i], n[i] - yes[i], yes[i] + 10], (x) => `${fmt(x)}명`, 5);
  return { key: 'count', data: table, ask: `${groups[i]} 응답자 가운데 찬성한 사람은 몇 명인가?`, ...ch, steps: [`${n[i]} × ${p[i]}% = ${fmt(yes[i])}명`] };
}

// ---- 막대그래프 ----
function chartQ(r: Rng, lv: number): Made {
  const labels = ['1월', '2월', '3월', '4월', '5월', '6월'];
  let v = int(r, 20, 60);
  const values = labels.map((_, i) => (i === 0 ? v : (v = Math.max(5, v + int(r, -12, 18)))));
  const chart: DataChart = { kind: 'chart', title: '월별 방문자 수', unit: '만 명', labels, values };
  const type = pick(r, ['up', ...(lv >= 4 ? ['avg'] : []), ...(lv >= 7 ? ['rate'] : [])]);
  if (type === 'up') {
    const inc = values.slice(1).map((x, i) => x - values[i]);
    const top = Math.max(...inc);
    if (top <= 0 || inc.filter((x) => x === top).length > 1) return null;
    const k = inc.indexOf(top) + 1;
    const others = shuffle(r, labels.filter((_, j) => j !== k && j > 0)).slice(0, 3);
    return { key: 'up', data: chart, ask: '방문자가 전달보다 가장 많이 늘어난 달은?', right: labels[k], wrong: others, steps: [`${labels[k]}: ${values[k - 1]} → ${values[k]} (+${top}만 명)`] };
  }
  if (type === 'avg') {
    const m = r1(values.reduce((a, b) => a + b, 0) / 6);
    const ch = numChoices(m, [(Math.max(...values) + Math.min(...values)) / 2, values.reduce((a, b) => a + b, 0) / 5], (x) => `${fmt(r1(x))}만 명`, 3);
    return { key: 'avg', data: chart, ask: '6개월 동안 한 달 평균 방문자 수는?', ...ch, steps: [`(${values.join(' + ')}) ÷ 6 = ${fmt(m)}만 명`] };
  }
  const a = values[2], b = values[5];
  if (a === b) return null;
  const g = r1(((b - a) / a) * 100);
  const ch = numChoices(Math.abs(g), [Math.abs(((b - a) / b) * 100), Math.abs(b - a)], (x) => `${fmt(r1(x))}%`, 4);
  return { key: 'rate', data: chart, ask: `3월 대비 6월 방문자 ${b > a ? '증가율' : '감소율'}은? (소수 첫째 자리까지)`, ...ch, steps: [`(${b} − ${a}) ÷ ${a} × 100 = ${fmt(g)}%`] };
}

const MAKERS = [regionQ, regionQ, productQ, surveyQ, chartQ];

export function makeDataRound(level: number, seed: number): DataQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const limitSec = 50 + lv * 5;
  const out: DataQuestion[] = [];
  for (let guard = 0; out.length < DATA_COUNT && guard < 300; guard++) {
    const m = pick(rng, MAKERS)(rng, lv);
    if (!m || m.wrong.length < 3 || out.some((q) => q.key === m.key && out.filter((x) => x.key === m.key).length >= 2)) continue;
    const options = shuffle(rng, [m.right, ...m.wrong.slice(0, 3)]);
    out.push({ key: m.key, data: m.data, ask: m.ask, options, answer: options.indexOf(m.right), n: 4, limitSec, steps: m.steps });
  }
  return out;
}
